import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Loader2, Download, Video, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { PalpitesData } from "./palpitesGenerator";
import {
  gerarVideoPalpites,
  gerarMiniaturaPalpites,
  baixarBlob,
  TrilhaId,
  FormatoVideo,
  SlidesModo,
  ModoDuracao,
} from "./videoRenderer";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  data: PalpitesData;
}

const GerarVideoModal: React.FC<Props> = ({ open, onOpenChange, data }) => {
  const [modoDuracao, setModoDuracao] = useState<ModoDuracao>("padrao");
  const [trilha, setTrilha] = useState<TrilhaId>("suspense");
  const [slides, setSlides] = useState<SlidesModo>("todos");
  const [formato, setFormato] = useState<FormatoVideo>("vertical");
  const [rendering, setRendering] = useState(false);
  const [thumbLoading, setThumbLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleGerar = async () => {
    setRendering(true);
    setProgress(0);
    try {
      const blob = await gerarVideoPalpites(data, {
        modoDuracao,
        trilha,
        slides,
        formato,
        onProgress: (p) => setProgress(Math.round(p * 100)),
      });
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      baixarBlob(blob, `palpite-guru-${ts}.webm`);
      toast.success("Vídeo gerado! Download iniciado.");
      onOpenChange(false);
    } catch (e) {
      console.error(e);
      toast.error("Erro ao gerar vídeo. Tente novamente.");
    } finally {
      setRendering(false);
      setProgress(0);
    }
  };

  const handleMiniatura = async () => {
    setThumbLoading(true);
    try {
      const blob = await gerarMiniaturaPalpites(data, formato);
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      baixarBlob(blob, `palpite-guru-miniatura-${ts}.png`);
      toast.success("Miniatura baixada!");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao gerar miniatura.");
    } finally {
      setThumbLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !rendering && onOpenChange(v)}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-yellow-500" />
            Gerar Vídeo do Palpite
          </DialogTitle>
          <DialogDescription>
            Renderização local no seu navegador. <b>Zero créditos de IA.</b>{" "}
            Formato ideal para YouTube Shorts, Reels e TikTok.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Duração por slide</Label>
            <Select value={modoDuracao} onValueChange={(v) => setModoDuracao(v as ModoDuracao)} disabled={rendering}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="padrao">Padrão (capa 20s + demais 40s cada)</SelectItem>
                <SelectItem value="rapido">Rápido (capa 10s + demais 20s cada)</SelectItem>
                <SelectItem value="curto15">Curto total 15s (uniforme)</SelectItem>
                <SelectItem value="curto30">Curto total 30s (uniforme)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Formato</Label>
            <Select value={formato} onValueChange={(v) => setFormato(v as FormatoVideo)} disabled={rendering}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vertical">Vertical 1080×1920 (Shorts / Reels / TikTok)</SelectItem>
                <SelectItem value="horizontal">Horizontal 1920×1080 (YouTube tradicional)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Trilha sonora (royalty-free)</Label>
            <Select value={trilha} onValueChange={(v) => setTrilha(v as TrilhaId)} disabled={rendering}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="suspense">Suspense Dourado (game-show)</SelectItem>
                <SelectItem value="urbana">Energia Urbana (beat leve)</SelectItem>
                <SelectItem value="vitoria">Vitória (orquestral)</SelectItem>
                <SelectItem value="nenhuma">Sem trilha</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Trilhas geradas ao vivo no navegador — 100% livres de direitos autorais.
            </p>
          </div>

          <div>
            <Label>Slides incluídos</Label>
            <Select value={slides} onValueChange={(v) => setSlides(v as SlidesModo)} disabled={rendering}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos (capa → CTA)</SelectItem>
                <SelectItem value="resumido">Resumido (capa + bicho + ouro + CTA)</SelectItem>
                <SelectItem value="ouro">Só palpites de ouro + CTA</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {rendering && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-xs text-center text-muted-foreground">
                Gravando... {progress}% — não feche esta janela
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={rendering}>
            Cancelar
          </Button>
          <Button
            variant="outline"
            onClick={handleMiniatura}
            disabled={rendering || thumbLoading}
            className="border-yellow-500 text-yellow-600 hover:bg-yellow-500/10"
          >
            {thumbLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando…</>
            ) : (
              <><ImageIcon className="w-4 h-4 mr-2" /> Baixar Miniatura (PNG)</>
            )}
          </Button>
          <Button
            onClick={handleGerar}
            disabled={rendering}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
          >
            {rendering ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Gerar e Baixar</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GerarVideoModal;
